import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, KanbanSquare, CheckCircle2, Receipt, ScrollText, Settings, LogOut,
  Search, Command as CmdIcon, ShieldCheck, Sparkles, FolderOpen, Landmark, TrendingUp,
  RefreshCw, BarChart3, Inbox, Clock, Building2, PieChart, UserCog, Wallet, FileSpreadsheet,
  FileText, Palette, AlertTriangle, CreditCard, Mail, ChevronsLeft, ChevronsRight, ArrowLeftRight,
  ExternalLink, FileSearch2, Menu, ChevronDown, Briefcase, Coins, Shield, LineChart, Cog,
  Database, BookOpen, KeyRound,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FeatureProvider, useFeature } from "@/lib/featureFlags";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import CommandPalette from "@/components/app/CommandPalette";
import ProductTour from "@/components/app/ProductTour";
import HelpDrawer from "@/components/app/HelpDrawer";

// Hybrid sidebar (iter100 · modern-simple pass):
//   - Expanded: collapsible accordion sections with a group icon on the left.
//   - Collapsed (icon rail): flat vertical list of icons only, tooltips on hover.
// The user requested "nice and simple" - grouped accordion is easier to scan
// than the previous flat wall of 30 items. Expansion state persists per group.
const NAV_GROUPS = [
  {
    key: "operations",
    label: "Operations",
    Icon: Briefcase,
    items: [
      { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", tour: "dashboard-nav" },
      { to: "/app/clients", label: "Counterparties", icon: Users, testid: "nav-clients", tour: "clients-nav" },
      { to: "/app/requests", label: "Requests", icon: KanbanSquare, testid: "nav-requests", tour: "requests-nav" },
      { to: "/app/approvals", label: "Approvals", icon: CheckCircle2, testid: "nav-approvals", tour: "approvals-nav" },
      { to: "/app/documents", label: "Documents", icon: FolderOpen, testid: "nav-documents" },
      { to: "/app/doc-reader", label: "Doc Reader", icon: FileSearch2, testid: "nav-doc-reader", featureKey: "ai_pro" },
    ],
  },
  {
    key: "money",
    label: "Money",
    Icon: Coins,
    items: [
      { to: "/app/invoicing", label: "Invoicing", icon: Receipt, testid: "nav-invoicing", r4: "R4_invoicing" },
      { to: "/app/money/accounts", label: "Accounts", icon: Landmark, testid: "nav-r6-accounts", r4: "R6_pages" },
      { to: "/app/money/transactions", label: "Transactions (new)", icon: Wallet, testid: "nav-r6-transactions", r4: "R6_pages" },
      { to: "/app/money/lending", label: "Lending (new)", icon: Coins, testid: "nav-r6-lending", r4: "R6_pages" },
      { to: "/app/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices", tour: "invoices-nav", r4Off: "R4_invoicing" },
      { to: "/app/bills", label: "Supplier invoices", icon: FileText, testid: "nav-bills", r4Off: "R4_invoicing" },
      { to: "/app/contracts", label: "Contracts", icon: ScrollText, testid: "nav-contracts" },
      { to: "/app/tenancies", label: "Tenancies", icon: KeyRound, testid: "nav-tenancies" },
      { to: "/app/reconciliation-scope", label: "Reconciliation Scope", icon: Landmark, testid: "nav-recon-scope" },
      { to: "/app/reconciliation", label: "Reconciliation Queue", icon: CheckCircle2, testid: "nav-recon-queue" },
      { to: "/app/role-review", label: "Director Role Review", icon: ScrollText, testid: "nav-role-review" },
      { to: "/app/safety-net", label: "Safety Net", icon: CheckCircle2, testid: "nav-safety-net" },
      { to: "/app/proformas", label: "Pro-formas", icon: FileText, testid: "nav-proformas", r4Off: "R4_invoicing" },
      { to: "/app/proformas/convert", label: "Convert to Invoice", icon: CheckCircle2, testid: "nav-proforma-convert", r4Off: "R4_invoicing" },
      { to: "/app/pricelist", label: "Pricelist", icon: ScrollText, testid: "nav-pricelist" },
      { to: "/app/issuers", label: "Issuers", icon: Building2, testid: "nav-issuers" },
      { to: "/app/transactions", label: "Transactions", icon: ArrowLeftRight, testid: "nav-transactions" },
      { to: "/app/bank-statements", label: "Bank Statements", icon: FileSpreadsheet, testid: "nav-bank-statements", featureKey: "ai_pro" },
      { to: "/app/banking", label: "Banking", icon: Landmark, testid: "nav-banking" },
      { to: "/app/banking/client-aua", label: "Client AUA", icon: Landmark, testid: "nav-client-aua" },
      { to: "/app/banking/review-queue", label: "Bank Review Queue", icon: Landmark, testid: "nav-bank-review-queue", r4Off: "R5_reconciliation" },
      { to: "/app/lending", label: "Lending", icon: TrendingUp, testid: "nav-lending" },
      { to: "/app/fx", label: "FX Rates", icon: RefreshCw, testid: "nav-fx" },
      { to: "/app/expenses", label: "Expenses", icon: Wallet, testid: "nav-expenses", r4Off: "R4_invoicing" },
      { to: "/app/crypto-wallets", label: "Crypto Wallets", icon: Coins, testid: "nav-crypto-wallets" },
      { to: "/app/crypto-analyzer", label: "Crypto Analyzer", icon: Coins, testid: "nav-crypto-analyzer" },
      { to: "/app/crypto-alerts", label: "Crypto Alerts", icon: Coins, testid: "nav-crypto-alerts" },
      { to: "/app/crypto-transactions", label: "Crypto Transactions", icon: Coins, testid: "nav-crypto-transactions" },
    ],
  },
  {
    key: "workforce",
    label: "Workforce",
    Icon: Briefcase,
    items: [
      { to: "/app/people", label: "People", icon: Users, testid: "nav-people" },
      { to: "/app/timesheets", label: "Timesheets", icon: Clock, testid: "nav-timesheets" },
      { to: "/app/payslips", label: "Payslips", icon: FileSpreadsheet, testid: "nav-payslips" },
    ],
  },
  {
    key: "compliance",
    label: "Compliance",
    Icon: Shield,
    items: [
      { to: "/app/compliance", label: "Compliance Suite", icon: ShieldCheck, testid: "nav-compliance" },
      { to: "/app/mail-connections", label: "Mail Scan", icon: Mail, testid: "nav-mail-connections" },
    ],
  },
  {
    key: "insights",
    label: "Insights",
    Icon: LineChart,
    items: [
      { to: "/app/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
      { to: "/app/dashboards", label: "KPI Builder", icon: PieChart, testid: "nav-dashboards" },
      { to: "/app/manual", label: "User Manual", icon: BookOpen, testid: "nav-manual" },
    ],
  },
  {
    key: "settings",
    label: "Firm Settings",
    Icon: Cog,
    items: [
      { to: "/app/admin", label: "Firm Settings", icon: Settings, testid: "nav-admin" },
      { to: "/app/portal-users", label: "Portal Users", icon: UserCog, testid: "nav-portal-users" },
      { to: "/app/brand", label: "Brand", icon: Palette, testid: "nav-brand" },
      { to: "/app/business-rules", label: "Business Rules", icon: Database, testid: "nav-business-rules" },
      { to: "/app/billing", label: "Billing", icon: CreditCard, testid: "nav-billing" },
      { to: "/app/tenant-admin", label: "Tenant Admin", icon: Building2, testid: "nav-tenant-admin" },
      { to: "/app/migration", label: "Migration Suite", icon: Database, testid: "nav-migration" },
    ],
  },
];

const OPEN_GROUPS_KEY = "formc_sidebar_open_groups_v2";

function readOpenGroups() {
  try {
    const raw = localStorage.getItem(OPEN_GROUPS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* localStorage disabled */ }
  // Default: only "Operations" open - keeps the sidebar tidy on first load.
  return { operations: true, money: false, compliance: false, insights: false, settings: false };
}

function writeOpenGroups(next) {
  try { localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(next)); } catch { /* localStorage disabled */ }
}

// Ensure any legacy `.dark` class from an older release is stripped on
// mount. Dark mode was retired 2026-02-28 per user feedback ("REMOVE DARK
// MODE"). We also purge the persisted preference so it can't come back.
if (typeof document !== "undefined") {
  document.documentElement.classList.remove("dark");
  try { localStorage.removeItem("szc_theme"); } catch { /* localStorage disabled */ }
}

function ThemeToggle() {
  // Dark mode retired 2026-02-28 - user requested a single, consistent
  // light palette. This component now renders nothing; kept as a stub in
  // case other files still reference the export via barrel imports.
  return null;
}

function NavItem({ to, label, Icon, testid, featureKey, onNavigate, collapsed = false, nested = false }) {
  const { enabled } = useFeature(featureKey || "");
  const gated = featureKey && !enabled;
  return (
    <NavLink
      to={to}
      onClick={onNavigate}
      data-testid={testid}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        `group flex items-center gap-3 ${collapsed ? "px-2 justify-center mx-2" : nested ? "pl-9 pr-3" : "px-3"} py-2 rounded-md text-[13px] transition-all ${
          isActive
            ? "bg-white/[0.08] text-white font-medium shadow-[inset_2px_0_0_0_rgba(91,140,255,1)]"
            : "text-white/60 hover:bg-white/[0.04] hover:text-white"
        }`
      }
    >
      <Icon className={`${collapsed ? "w-4 h-4" : "w-3.5 h-3.5"} shrink-0`} strokeWidth={1.8} />
      {!collapsed && <span className="flex-1 truncate">{label}</span>}
      {!collapsed && gated && (
        <span data-testid={`${testid}-pro-badge`} className="text-xs tracking-wider uppercase px-1.5 py-0.5 rounded-full bg-[#5B8CFF]/15 text-[#a9c1ff] font-semibold">Pro</span>
      )}
    </NavLink>
  );
}

function GroupHeader({ group, isOpen, onToggle, active }) {
  const { Icon } = group;
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={`nav-group-${group.key}`}
      aria-expanded={isOpen}
      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs uppercase tracking-[0.12em] font-semibold transition-colors ${
        active ? "text-white" : "text-white/45 hover:text-white/80 hover:bg-white/[0.03]"
      }`}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
      <span className="flex-1 text-left">{group.label}</span>
      <ChevronDown
        className={`w-3.5 h-3.5 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-0" : "-rotate-90"}`}
        strokeWidth={2}
      />
    </button>
  );
}

// Step 8 (design note): 15 items, shown when R6_sidebar is on; ADMIN group for super admin only.
const NAV_R6 = [
  { key: "r6-work", label: "Work", Icon: Briefcase, items: [
    { to: "/app/our-company", label: "Our company", icon: Building2, testid: "nav8-our-company" },
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav8-dashboard" },
    { to: "/app/clients", label: "Counterparties", icon: Users, testid: "nav8-counterparties" },
    { to: "/app/hub/requests", label: "Requests", icon: KanbanSquare, testid: "nav8-requests" },
    { to: "/app/hub/documents", label: "Documents", icon: FolderOpen, testid: "nav8-documents" },
    { to: "/app/contracts", label: "Contracts", icon: ScrollText, testid: "nav8-contracts" },
    { to: "/app/tenancies", label: "Tenancies", icon: KeyRound, testid: "nav8-tenancies" },
  ] },
  { key: "r6-money", label: "Money", Icon: Coins, items: [
    { to: "/app/invoicing", label: "Invoicing", icon: Receipt, testid: "nav8-invoicing" },
    { to: "/app/costs", label: "Costs", icon: Wallet, testid: "nav8-costs" },
    { to: "/app/money/accounts", label: "Accounts", icon: Landmark, testid: "nav8-accounts" },
    { to: "/app/money/transactions", label: "Transactions", icon: ArrowLeftRight, testid: "nav8-transactions" },
    { to: "/app/money/lending", label: "Lending", icon: TrendingUp, testid: "nav8-lending" },
    { to: "/app/reconciliation", label: "Reconciliation", icon: CheckCircle2, testid: "nav8-reconciliation" },
  ] },
  { key: "r6-people", label: "People", Icon: Users, items: [{ to: "/app/hub/people", label: "People", icon: Users, testid: "nav8-people" }] },
  { key: "r6-compliance", label: "Compliance", Icon: ShieldCheck, items: [{ to: "/app/hub/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav8-compliance" }] },
  { key: "r6-settings", label: "Settings", Icon: Cog, items: [{ to: "/app/hub/settings", label: "Settings", icon: Settings, testid: "nav8-settings" }] },
  { key: "r6-admin", label: "Admin", Icon: Shield, superOnly: true, items: [
    { to: "/app/safety-net", label: "Safety Net", icon: Shield, testid: "nav8-safety-net" },
    { to: "/app/hub/data-review", label: "Data review", icon: Database, testid: "nav8-data-review" },
    { to: "/app/tenant-admin", label: "Tenant Admin", icon: Building2, testid: "nav8-tenant-admin" },
    { to: "/app/migration", label: "Migration Suite", icon: Database, testid: "nav8-migration" },
  ] },
];

function useNavGroups() {
  const { user } = useAuth();
  return useMemo(() => NAV_R6.filter((g) => !g.superOnly || user?.role === "super_admin"), [user]);
}

function SidebarNav({ onNavigate, onOpenPalette, collapsed = false, onToggleCollapse, openGroups, onToggleGroup, activeGroupKey }) {
  const brand = useBrand();
  const navGroups = useNavGroups();
  const initials = (brand.firm_name || "FC").split(/\s+/).map(w => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <>
      {/* Brand block */}
      <div className={`${collapsed ? "px-3 py-4" : "px-5 py-5"} border-b border-white/[0.06] flex items-center ${collapsed ? "justify-center" : "justify-between"} gap-2`}>
        <Link to="/app/dashboard" onClick={onNavigate} className="flex items-center gap-3 min-w-0" data-testid="app-brand">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.firm_name} className="w-9 h-9 object-contain rounded-lg shrink-0" style={{ backgroundColor: "rgba(255,255,255,0.06)" }} />
          ) : (
            <div className="w-9 h-9 rounded-lg flex items-center justify-center tracking-tighter shrink-0" style={{ background: `linear-gradient(135deg, ${brand.primary_color}, ${brand.accent_color})`, boxShadow: "0 6px 18px rgba(91,140,255,0.25)" }}>
              <span className="text-white text-sm font-extrabold">{initials}</span>
            </div>
          )}
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-serif text-[17px] leading-none truncate text-white">{brand.firm_name}</div>
              <div className="text-xs tracking-[0.12em] uppercase text-white/40 mt-1">Operations</div>
            </div>
          )}
        </Link>
        {onToggleCollapse && !collapsed && (
          <button
            onClick={onToggleCollapse}
            data-testid="sidebar-collapse-btn"
            aria-label="Collapse sidebar"
            className="hidden lg:inline-flex w-7 h-7 rounded-md items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.08] shrink-0 transition"
            title="Collapse sidebar"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
        )}
      </div>

      {onToggleCollapse && collapsed && (
        <div className="px-2 pt-2">
          <button
            onClick={onToggleCollapse}
            data-testid="sidebar-expand-btn"
            aria-label="Expand sidebar"
            className="hidden lg:flex w-full items-center justify-center py-2 rounded-md text-white/40 hover:text-white hover:bg-white/[0.06] transition"
            title="Expand sidebar"
          >
            <ChevronsRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Nav */}
      <nav className={`flex-1 ${collapsed ? "py-3" : "px-2 py-3"} overflow-y-auto`}>
        {collapsed ? (
          // Icon rail - flatten all items into one column
          <div className="space-y-0.5">
            {navGroups.map((group, gi) => (
              <div key={group.key} className={gi === 0 ? "" : "mt-2 pt-2 border-t border-white/[0.05] mx-3"}>
                {group.items.map(({ to, label, icon: Icon, testid, featureKey }) => (
                  <NavItem key={to} to={to} label={label} Icon={Icon} testid={testid} featureKey={featureKey} onNavigate={onNavigate} collapsed />
                ))}
              </div>
            ))}
          </div>
        ) : (
          // Expanded - accordion groups
          <div className="space-y-1" data-testid="sidebar-groups">
            {navGroups.map(group => {
              const isOpen = openGroups[group.key];
              const isActive = activeGroupKey === group.key;
              return (
                <div key={group.key} className="space-y-0.5">
                  <GroupHeader group={group} isOpen={isOpen} active={isActive} onToggle={() => onToggleGroup(group.key)} />
                  {isOpen && (
                    <div className="space-y-0.5 pb-1" data-testid={`nav-group-${group.key}-items`}>
                      {group.items.map(({ to, label, icon: Icon, testid, featureKey }) => (
                        <NavItem key={to} to={to} label={label} Icon={Icon} testid={testid} featureKey={featureKey} onNavigate={onNavigate} nested />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="pt-2 mt-2 border-t border-white/[0.05]">
              <a
                href="/portal"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nav-client-portal"
                onClick={onNavigate}
                className="flex items-center gap-3 px-3 py-2 rounded-md text-[13px] text-[#a9c1ff] hover:bg-white/[0.04] hover:text-white transition-all"
              >
                <ExternalLink className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} />
                <span className="flex-1">Client Portal</span>
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Search */}
      <div className={`${collapsed ? "p-2" : "p-3"} border-t border-white/[0.06]`}>
        <button
          onClick={() => { onNavigate?.(); onOpenPalette(); }}
          data-testid="open-command-palette"
          data-tour="command"
          title={collapsed ? "Search (⌘K)" : undefined}
          className={`w-full flex items-center ${collapsed ? "justify-center" : "justify-between"} gap-2 px-3 py-2 rounded-md bg-white/[0.04] hover:bg-white/[0.08] text-white/60 hover:text-white text-[13px] transition-colors border border-white/[0.05]`}
        >
          {collapsed ? (
            <Search className="w-4 h-4" />
          ) : (
            <>
              <span className="flex items-center gap-2"><Search className="w-3.5 h-3.5" /> Search…</span>
              <kbd className="text-[10px] tracking-wider px-1.5 py-0.5 border border-white/15 rounded font-mono">⌘K</kbd>
            </>
          )}
        </button>
      </div>
    </>
  );
}

export default function AppShell() {
  const { user, loading, logout, checkAuth } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [tourForced, setTourForced] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("formc_sidebar_collapsed") === "1"; }
    catch { return false; }
  });
  const toggleCollapsed = () => {
    setCollapsed(v => {
      const next = !v;
      try { localStorage.setItem("formc_sidebar_collapsed", next ? "1" : "0"); } catch { /* localStorage disabled */ }
      return next;
    });
  };

  // Hoisted accordion state - SINGLE source of truth for both the desktop
  // <aside> and the mobile <Sheet>. Toggling a group in either instance
  // now reflects live in the other, and localStorage stays consistent.
  const [openGroups, setOpenGroups] = useState(readOpenGroups);

  const activeGroupKey = useMemo(() => {
    const path = location.pathname;
    for (const g of [...NAV_R6, ...NAV_GROUPS]) {
      if (g.items.some(i => path === i.to || path.startsWith(i.to + "/"))) return g.key;
    }
    return null;
  }, [location.pathname]);

  // Auto-open the active route's group - but only ONCE per route change,
  // so the user can manually collapse it and it stays collapsed.
  const lastAutoOpenedRef = useRef(null);
  useEffect(() => {
    if (activeGroupKey && lastAutoOpenedRef.current !== activeGroupKey) {
      lastAutoOpenedRef.current = activeGroupKey;
      setOpenGroups(prev => {
        if (prev[activeGroupKey]) return prev;
        const next = { ...prev, [activeGroupKey]: true };
        writeOpenGroups(next);
        return next;
      });
    }
  }, [activeGroupKey]);

  const toggleGroup = (key) => {
    setOpenGroups(prev => {
      const next = { ...prev, [key]: !prev[key] };
      writeOpenGroups(next);
      return next;
    });
  };

  const endImpersonation = async () => {
    try {
      const { data } = await api.post("/auth/end-impersonation");
      await checkAuth();
      navigate(data.next || "/super-admin");
      toast.success("Impersonation ended");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to end impersonation");
    }
  };

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center text-white">
        <div className="w-10 h-10 border-2 border-[#5B8CFF]/30 border-t-[#5B8CFF] rounded-full animate-spin" />
      </div>
    );
  }

  const initials = (user.name || user.email || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();
  const roleLabel = user.role.replaceAll("_", " ");

  return (
    <FeatureProvider>
    <div className="min-h-screen bg-[#F7F7F5] flex">
      {user?.impersonated_by && (
        <div data-testid="impersonation-banner" className="fixed top-0 inset-x-0 z-30 bg-violet-600 text-white text-xs px-4 py-2 flex items-center justify-center gap-4 shadow-lg">
          <span className="font-mono uppercase tracking-wider">⚠ Impersonating {user.email} · originated by {user.impersonated_by.email}</span>
          <button onClick={endImpersonation} data-testid="end-impersonation-btn" className="px-3 py-1 rounded bg-white/15 hover:bg-white/25 transition-colors">
            End impersonation
          </button>
        </div>
      )}

      {/* Left rail - modern deep-navy with subtle gradient overlay */}
      <aside
        data-tour="sidebar"
        data-testid="app-sidebar"
        className={`hidden lg:flex flex-col ${collapsed ? "w-[60px]" : "w-[248px]"} fixed inset-y-0 left-0 z-20 transition-[width] duration-200`}
        style={{
          background: "linear-gradient(180deg, #0b1327 0%, #08101f 100%)",
          borderRight: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <SidebarNav
          onOpenPalette={() => setPaletteOpen(true)}
          collapsed={collapsed}
          onToggleCollapse={toggleCollapsed}
          openGroups={openGroups}
          onToggleGroup={toggleGroup}
          activeGroupKey={activeGroupKey}
        />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
        <SheetContent
          side="left"
          className="w-72 p-0 border-r border-white/[0.06] flex flex-col [&>button]:text-white/70 [&>button]:hover:text-white"
          style={{ background: "linear-gradient(180deg, #0b1327 0%, #08101f 100%)", color: "white" }}
          data-testid="mobile-nav-drawer"
        >
          <VisuallyHidden asChild>
            <SheetTitle>Navigation</SheetTitle>
          </VisuallyHidden>
          <VisuallyHidden asChild>
            <SheetDescription>Primary navigation for the FORM C back-office.</SheetDescription>
          </VisuallyHidden>
          <SidebarNav
            onNavigate={() => setMobileNavOpen(false)}
            onOpenPalette={() => setPaletteOpen(true)}
            openGroups={openGroups}
            onToggleGroup={toggleGroup}
            activeGroupKey={activeGroupKey}
          />
        </SheetContent>
      </Sheet>

      {/* Main */}
      <div className={`flex-1 ${collapsed ? "lg:pl-[60px]" : "lg:pl-[248px]"} flex flex-col min-h-screen min-w-0 transition-[padding] duration-200`}>
        <header className="h-16 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 flex items-center justify-between px-4 md:px-6 sticky top-0 z-10">
          <div className="flex items-center gap-2 md:gap-4 min-w-0">
            <button
              onClick={() => setMobileNavOpen(true)}
              data-testid="mobile-nav-toggle"
              aria-label="Open navigation"
              className="lg:hidden w-9 h-9 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-700 shrink-0"
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="text-sm text-slate-500 truncate">
              <span className="hidden md:inline">{brand.firm_name}&nbsp;· </span>
              <span className="text-slate-900 font-medium capitalize" data-testid="topbar-crumb">{(() => {
                const segs = location.pathname.split("/").filter(Boolean);
                const last = segs.slice(-1)[0] || "dashboard";
                if (!/^[a-z]+_[0-9a-z]{6,}$/i.test(last)) return last.replace(/-/g, " ");
                const parent = segs.slice(-2)[0] || "";
                return { clients: "Counterparty", contracts: "Contract", documents: "Document", requests: "Request" }[parent] || "Details";
              })()}</span>
            </div>
          </div>
          {user?.tenant?.status === "suspended" && (
            <div className="hidden lg:flex items-center gap-2 mx-4 px-3 py-1.5 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs" data-testid="tenant-suspended-banner">
              <AlertTriangle className="w-3.5 h-3.5" />
              <span>Workspace is read-only · contact your account manager</span>
            </div>
          )}
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            {(
              <Link to="/app/manual" data-testid="header-user-manual-link" title="User Manual"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:border-[#5B8CFF] hover:text-[#5B8CFF] text-xs font-medium transition-colors">
                <BookOpen className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Help</span>
              </Link>
            )}
            {!user.mfa_enabled && (
              <Link to="/app/mfa" data-testid="enable-mfa-link" className="hidden sm:block">
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-200 border-0 gap-1.5 cursor-pointer">
                  <ShieldCheck className="w-3 h-3" /> Enable MFA
                </Badge>
              </Link>
            )}
            <a
              href="/portal"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-client-portal-link"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 bg-white text-slate-700 hover:border-[#5B8CFF] hover:text-[#5B8CFF] text-xs font-medium transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span className="hidden md:inline">Client Portal</span>
              <span className="md:hidden">Portal</span>
            </a>
            <a
              href="/portal"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-client-portal-icon"
              aria-label="Open client portal"
              className="sm:hidden w-9 h-9 rounded-md border border-slate-200 bg-white text-slate-700 hover:border-[#5B8CFF] hover:text-[#5B8CFF] flex items-center justify-center"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
            <button onClick={() => setPaletteOpen(true)} data-testid="header-search" className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 text-slate-500 hover:border-[#5B8CFF] hover:text-slate-900 text-sm">
              <CmdIcon className="w-3.5 h-3.5" /> <span>Search</span>
              <kbd className="text-[10px] px-1 border rounded ml-2">⌘K</kbd>
            </button>
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="focus:outline-none">
                <Avatar className="w-9 h-9 border border-slate-200">
                  {user.picture && <AvatarImage src={user.picture} alt={user.name} />}
                  <AvatarFallback className="bg-[#0b1327] text-[#a9c1ff] text-xs font-semibold">{initials}</AvatarFallback>
                </Avatar>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold text-slate-900">{user.name}</div>
                  <div className="text-xs text-slate-500 font-normal">{user.email}</div>
                  <div className="mt-2 flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider capitalize">{roleLabel}</Badge>
                    {user.mfa_enabled && <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border-0 text-[10px]">MFA</Badge>}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-mfa" onClick={() => navigate("/app/mfa")}>
                  <ShieldCheck className="w-4 h-4 mr-2" /> MFA settings
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-restart-tour" onClick={() => { localStorage.removeItem("formc_tour_v1_done"); setTourForced(t => t + 1); }}>
                  <Sparkles className="w-4 h-4 mr-2" /> Replay product tour
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={logout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 lg:p-8 min-w-0">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <ProductTour key={tourForced} forceOpen={tourForced > 0} onClose={() => setTourForced(0)} />
      <HelpDrawer />
    </div>
    </FeatureProvider>
  );
}
